import React, { useState } from 'react';
import { Modal, Input, Button, Radio, Space, Typography, message } from 'antd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import ReactDOM from 'react-dom';

// Import default templates
import defaultPropertyConfig from './defaultPropertyConfig';
import defaultSampleHelper from './defaultSampleHelper';
import defaultEnvFile from './defaultEnvFile';
import defaultEmailTemplate from './defaultEmailTemplate';
import defaultAiQuestions from './defaultAiQuestions';
import defaultReports from './defaultReports';

const TenantGeneratorModal = ({ currentConfig, onClose }) => {
    const [newTenantName, setNewTenantName] = useState('');
    const [useCurrentConfig, setUseCurrentConfig] = useState(true);

    // Helper function for sanitizing tenant names
    const sanitizeTenantName = (name) => {
        return name.toLowerCase()
            .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric with hyphen
            .replace(/-+/g, '-')         // Replace multiple hyphens with a single one
            .replace(/^-|-$/g, '');      // Remove hyphens at the beginning or end
    };

    // Helper function for formatting tenant names for display
    const formatTenantName = (name) => {
        if (!name) return '';
        // Convert kebab-case to Title Case (e.g., "my-tenant" to "My Tenant")
        return name.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Function to create and download the tenant zip file
    const createTenantZip = async () => {
        if (!newTenantName.trim()) {
            message.error('Please enter a tenant name');
            return;
        }

        try {
            const sanitizedName = sanitizeTenantName(newTenantName);
            const formattedName = formatTenantName(sanitizedName);

            // Create a new JSZip instance
            const zip = new JSZip();

            // Add files to the zip with tenant placeholders replaced
            const propertyConfigContent = defaultPropertyConfig
                .replace(/\{tenant-id\}/g, sanitizedName)
                .replace(/\{tenant-name\}/g, formattedName);
            zip.file("property-config.json", propertyConfigContent);

            const sampleHelperContent = defaultSampleHelper
                .replace(/\{tenant-id\}/g, sanitizedName)
                .replace(/\{tenant-name\}/g, formattedName);
            zip.file("SampleHelper.js", sampleHelperContent);

            const sampleReportsContent = defaultReports;
            zip.file("reports.json", sampleReportsContent);

            // Decide which content to use for AI questions, email template, and call tags
            if (useCurrentConfig && currentConfig) {
                // Use current configuration with tenant name replacements
                zip.file("ai-questions.json", currentConfig.aiQuestions);

                const emailTemplateContent = currentConfig.emailTemplate
                    .replace(/\{tenant-id\}/g, sanitizedName)
                    .replace(/\{tenant-name\}/g, formattedName);
                zip.file("email-template.html", emailTemplateContent);

                // Extract tag expressions from current configuration and generate CALL_TAGS env var
                let callTags = '';
                if (currentConfig.tagExpressions && Array.isArray(currentConfig.tagExpressions)) {
                    callTags = currentConfig.tagExpressions.join(',');
                }

                // Create a custom env file with the current tag expressions
                const envFileContent = defaultEnvFile
                    .replace(/\{tenant-id\}/g, sanitizedName)
                    .replace(/\{tenant-name\}/g, formattedName)
                    .replace(/CALL_TAGS=.+$/m, `CALL_TAGS=${callTags}`);

                zip.file(`.env.${sanitizedName}`, envFileContent);
            } else {
                // Use default configuration with tenant name replacements
                const aiQuestionsContent = defaultAiQuestions
                    .replace(/\{tenant-id\}/g, sanitizedName)
                    .replace(/\{tenant-name\}/g, formattedName);
                zip.file("ai-questions.json", aiQuestionsContent);

                const emailTemplateContent = defaultEmailTemplate
                    .replace(/\{tenant-id\}/g, sanitizedName)
                    .replace(/\{tenant-name\}/g, formattedName);
                zip.file("email-template.html", emailTemplateContent);

                // Use default env file
                const envFileContent = defaultEnvFile
                    .replace(/\{tenant-id\}/g, sanitizedName)
                    .replace(/\{tenant-name\}/g, formattedName);
                zip.file(`.env.${sanitizedName}`, envFileContent);
            }

            // Generate the zip file
            const content = await zip.generateAsync({ type: "blob" });

            // Save the zip file
            saveAs(content, `${sanitizedName}.zip`);

            // Close the modal
            onClose();

            message.success(`Tenant configuration for "${formattedName}" has been generated and downloaded`);

        } catch (error) {
            console.error('Error generating tenant configuration:', error);
            message.error('Error generating tenant configuration');
        }
    };

    return (
        <Modal
            title="Generate New Tenant Configuration"
            visible={true}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="generate" type="primary" onClick={createTenantZip}   style={{
                    backgroundColor: '#1e7ddc',
                    borderColor: '#1e7ddc'
                }}>
                    Generate and Download
                </Button>
            ]}
        >
            <div style={{ marginBottom: '16px' }}>
                <Typography.Text strong>Tenant Name:</Typography.Text>
                <Input
                    placeholder="Enter tenant name (e.g., 'Lead Lens AI Client')"
                    value={newTenantName}
                    onChange={(e) => setNewTenantName(e.target.value)}
                    style={{ marginTop: '8px' }}
                />
                {newTenantName && (
                    <Typography.Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
                        Will be saved as: {sanitizeTenantName(newTenantName)}
                    </Typography.Text>
                )}
            </div>

            <div>
                <Typography.Text strong>Configuration:</Typography.Text>
                <Radio.Group
                    value={useCurrentConfig}
                    onChange={(e) => setUseCurrentConfig(e.target.value)}
                    style={{ marginTop: '8px', display: 'block' }}
                >
                    <Space direction="vertical">
                        <Radio value={true}>Use Current Configuration</Radio>
                        <Radio value={false}>Use Default Configuration</Radio>
                    </Space>
                </Radio.Group>
            </div>
        </Modal>
    );
};

// Export a utility object with methods to show the modal
const TenantGenerator = {
    showTenantGeneratorModal: (currentConfig) => {
        // Create a div to mount the modal
        const div = document.createElement('div');
        document.body.appendChild(div);

        // Function to unmount and clean up
        const destroyModal = () => {
            ReactDOM.unmountComponentAtNode(div);
            document.body.removeChild(div);
        };

        // Render the modal
        ReactDOM.render(
            <TenantGeneratorModal
                currentConfig={currentConfig}
                onClose={destroyModal}
            />,
            div
        );
    }
};

export default TenantGenerator;
